import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatPanel } from '../chat/ChatPanel.js';
import type { ChatConfig, DashboardContext } from '../../../types/chat.js';

interface KpiChatPanelProps {
  /** 選択中の組織/チーム */
  selectedTeam?: DashboardContext['team'];
  /** 選択中の期間（日数） */
  selectedDays?: number;
}

/**
 * KPIチャットパネル
 * KPIページ専用のチャットアシスタント
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business KPIチャットパネル。選択中のチーム・期間コンテキストをChatPanelに渡し、KPIデータに特化したAI分析チャットを提供
 * @graph-connects ChatPanel [calls] 汎用チャットパネルコンポーネント呼び出し
 */
export function KpiChatPanel({ selectedTeam, selectedDays }: KpiChatPanelProps) {
  const { t } = useTranslation();

  const config: ChatConfig = useMemo(
    () => ({
      chatType: 'kpi',
      endpoint: '/chat/kpi',
      allowedRoles: ['admin', 'group_manager', 'team_leader'] as const,
      title: t('chat.kpi.title', 'KPI Assistant'),
      placeholder: t('chat.kpi.placeholder', 'KPIについて質問...'),
      welcomeMessage: t(
        'chat.kpi.welcome',
        'KPIデータについて質問できます。バグ発生率、リリースポイント、開発ポイントなど、様々な指標を分析します。',
      ),
      dashboardContext: {
        team: selectedTeam,
        days: selectedDays,
      },
    }),
    [t, selectedTeam, selectedDays],
  );

  return <ChatPanel config={config} />;
}
